import localConfig from '~/config/locale-config'

export type HeaderData = {
  script?: Array<{ innerHTML: string }>
}

export const deleteUndefined = (obj: any) => {
  if (obj) Object.keys(obj).forEach((key) => obj[key] === undefined && delete obj[key])
  return obj
}

export const getSeoPanelData = (seoPanel: any, includes: any) => {
  if (!seoPanel) return null

  const id = seoPanel.sys?.id
  if (!id) return null
  const fields = seoPanel.fields || includes?.Entry?.find(({ sys }: { sys: any }) => sys.id === id)?.fields
  if (!fields) return null

  const { title, description, keywords, canonical, noindex } = fields
  return deleteUndefined({
    id,
    contentType: 'seoPanel',
    title,
    description,
    keywords,
    canonical,
    noindex,
  })
}

export type SEOProps =
  | {
      title?: string
      description?: string
      keywords?: string
      canonical?: string
      noindex?: boolean
    }
  | undefined

export type ShareImage = { url: string; width: number; height: number } | null

/**
 * Resolves the request origin with fallbacks for SSR, client, and lazy-getter contexts
 * where the Nuxt AsyncLocalStorage context may be unavailable.
 * @param requestOrigin - Pre-resolved origin from component setup(); takes priority when provided.
 */
export const resolveRequestOrigin = (requestOrigin?: string): string => {
  if (requestOrigin) return requestOrigin
  if (import.meta.server) {
    try {
      return useRequestURL()?.origin ?? ''
    } catch {
      // Called outside Nuxt context (e.g. inside a lazy useHead getter resolved by @unhead/vue)
      return ''
    }
  }
  return window.location.origin
}

type getHeaderDataArgs = {
  seo?: SEOProps
  shareImage?: ShareImage
  locale: Locales
  path: string
  gtmPageType?: string
}

export function getHeaderData({ seo, shareImage, locale, path }: getHeaderDataArgs) {
  if (!seo) return {}

  const domain = `https://${localConfig[locale].domain}`
  let canonical = seo.canonical || `${domain}${path}`
  if (canonical && (canonical.startsWith('/p/') || canonical.startsWith(`/${localConfig[locale].pathPrefix}/p/`))) {
    canonical = `${domain}${canonical}`
  }

  return {
    title: seo.title,
    meta: [
      { hid: 'description', name: 'description', content: seo.description },
      { hid: 'robots', name: 'robots', content: seo.noindex ? 'noindex' : 'index' },
      {
        hid: 'og:site_name',
        name: 'og:site_name',
        property: 'og:site_name',
        content: 'Michael Hill Jewellers',
      },
      { hid: 'og:title', property: 'og:title', content: seo.title },
      { hid: 'og:description', property: 'og:description', content: seo.description },
      {
        hid: 'og:image',
        property: 'og:image',
        content: shareImage?.url || `${domain}/icon.png`,
      },
      { hid: 'og:image:width', property: 'og:image:width', content: shareImage?.width },
      { hid: 'og:image:height', property: 'og:image:height', content: shareImage?.height },
      { hid: 'og:type', property: 'og:type', content: 'article' },
      { hid: 'og:url', property: 'og:url', content: canonical },
      { hid: 'twitter:card', name: 'twitter:card', content: 'summary_large_image' },
      { hid: 'twitter:title', name: 'twitter:title', content: seo.title },
      { hid: 'twitter:description', name: 'twitter:description', content: seo.description },
      {
        hid: 'twitter:image',
        name: 'twitter:image',
        content: shareImage?.url || `${domain}/icon.png`,
      },
    ],
    link: [{ rel: 'canonical', href: canonical }],
  }
}

export type DefaultHeaderDataArgs = SEOProps & getHeaderDataArgs & { script?: object[]; loggedIn?: boolean }

export const getDefaultHeaderData = ({ gtmPageType, loggedIn, locale, path, ...seo }: DefaultHeaderDataArgs) => {
  const headerData = getHeaderData({ seo, shareImage: seo?.shareImage, locale, path })

  addGtmScripts(headerData as HeaderData, gtmPageType, loggedIn)

  return headerData
}

// `loggedIn` must be passed in by the caller — never read from a Pinia store here. getDefaultHeaderData
// is resolved inside a lazy useHead getter, which unhead v2 (Nuxt 4) evaluates during renderSSRHead,
// outside any component instance and with no active Pinia on the server. Calling useUserStore() at that
// point throws "getActivePinia() was called but there was no active Pinia". useSeoHead() resolves
// loggedIn from the user store at component setup (where Pinia is active) and threads it through here.
const addGtmScripts = (headerData: HeaderData, gtmPageType?: string, loggedIn = false) => {
  if (!gtmPageType) {
    return
  }

  const loggedInLabel = loggedIn ? 'True' : 'False'
  headerData.script = headerData.script || []
  headerData.script.push({
    innerHTML: `var google_tag_params = { ecomm_pagetype: "${gtmPageType}" }; var gtmData = { loggedIn: "${loggedInLabel}", ecomm_pagetype: "${gtmPageType}" };`,
  })
}
